import { Injectable } from '@angular/core';
import {
  ClubSportEntry,
  SPORT_LABELS,
  SportConfig,
  SportType,
  TenantConfig,
} from '@models';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface SportOption {
  label: string;
  value: SportType;
}

@Injectable({
  providedIn: 'root',
})
export class SportContextService {
  private availableSportsSubject = new BehaviorSubject<SportType[]>([]);
  private activeSportSubject = new BehaviorSubject<SportType | null>(null);
  /** clubSports entries stored when the tenant is a multi-sport club */
  private clubSportsSubject = new BehaviorSubject<ClubSportEntry[]>([]);

  /** List of sports the current tenant manages */
  readonly availableSports$ = this.availableSportsSubject.asObservable();

  /** Currently selected sport — null until initialized */
  readonly activeSport$ = this.activeSportSubject.asObservable();

  /** True when the tenant manages more than one sport (show selector) */
  readonly isMultiSport$ = this.availableSportsSubject.pipe(
    map((sports) => sports.length > 1),
  );

  readonly sportOptions$ = this.availableSportsSubject.pipe(
    map((sports): SportOption[] =>
      sports.map((s) => ({ label: SPORT_LABELS[s], value: s })),
    ),
  );

  /**
   * Called after tenant config is loaded (e.g. from TenantContextService).
   * Sets available sports and defaults to the primary sport.
   */
  initFromTenantConfig(config: TenantConfig): void {
    let all: SportType[];

    if (config.clubSports?.length) {
      // Multi-sport club: derive sport list from clubSports entries
      // Ensure primary sport is always first
      const primary = config.sport;
      all = [
        primary,
        ...config.clubSports.map((e) => e.sport).filter((s) => s !== primary),
      ];
      this.clubSportsSubject.next([...config.clubSports]);
    } else {
      all = [config.sport];
      this.clubSportsSubject.next([]);
    }

    this.availableSportsSubject.next(all);

    // Keep current selection if still valid; otherwise default to primary
    const current = this.activeSportSubject.getValue();
    if (!current || !all.includes(current)) {
      this.activeSportSubject.next(config.sport);
    }
  }

  setActiveSport(sport: SportType): void {
    this.activeSportSubject.next(sport);
  }

  getActiveSport(): SportType | null {
    return this.activeSportSubject.getValue();
  }

  /**
   * Returns the SportConfig for the given sport.
   * For multi-sport clubs, returns the per-sport config from clubSports.
   * Falls back to the provided defaultConfig (usually TenantConfig.sportConfig).
   */
  getSportConfig(sport: SportType, defaultConfig: SportConfig): SportConfig {
    const entries = this.clubSportsSubject.getValue();
    return entries.find((e) => e.sport === sport)?.sportConfig ?? defaultConfig;
  }

  /**
   * Returns the federation tenant ID linked to the given sport, if any.
   * Only relevant for club tenants with clubSports.
   */
  getFederationIdForSport(sport: SportType): string | undefined {
    return this.clubSportsSubject.getValue().find((e) => e.sport === sport)
      ?.federationId;
  }

  clear(): void {
    this.availableSportsSubject.next([]);
    this.activeSportSubject.next(null);
    this.clubSportsSubject.next([]);
  }
}
